// Espinha de cores — eixo de navegacao da ficha do produto (W5, plano
// secao 5 W5 + README secao "Ficha de produto"). Um so componente serve os
// dois tamanhos: desktop (coluna sticky de rolagem propria, dentro de um
// <aside>) e mobile (mesmo corpo dentro do Drawer aberto pela ctx-bar).
//
// Nao busca dado sozinho: recebe as linhas ja calculadas (ProductPage junta
// sisplan.colors_active + fotos por cor + grid) e so cuida de busca, filtro,
// selecao multipla e navegacao Base/Cor.
import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { ColorSwatch } from "./ColorSwatch";
import { Button } from "../ui/button";
import { Select } from "../ui/select";
import { cn } from "../../lib/utils";

export interface ColorSpineRow {
  name: string;
  hasOwnPhoto: boolean;
  /** Quantas fotos proprias esta cor tem (0 = "sem foto propria"). */
  photoCount: number;
  stock: number;
  disabledInChannel: boolean;
}

type PalFilter = "all" | "no-photo" | "with-photo" | "disabled";

export interface ColorSpineProps {
  rows: ColorSpineRow[];
  totalInSisplan: number;
  activeContext: "base" | string;
  onSelectContext: (ctx: "base" | string) => void;
  selected: Set<string>;
  onToggleSelected: (name: string) => void;
  onClearSelected: () => void;
  onBatchEnable: () => void;
  onBatchDisable: () => void;
  onBatchCopyPhotos: (sourceColor: string) => void;
  disabled: boolean;
  /** true = coluna desktop sticky; false = corpo dentro de Drawer (mobile). */
  desktop?: boolean;
}

const FILTERS: { value: PalFilter; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "no-photo", label: "Sem foto" },
  { value: "with-photo", label: "Com foto" },
  { value: "disabled", label: "Desligadas" },
];

export function ColorSpine({
  rows,
  totalInSisplan,
  activeContext,
  onSelectContext,
  selected,
  onToggleSelected,
  onClearSelected,
  onBatchEnable,
  onBatchDisable,
  onBatchCopyPhotos,
  disabled,
  desktop = true,
}: ColorSpineProps): JSX.Element {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<PalFilter>("all");
  const [copySource, setCopySource] = useState("");

  const semFoto = rows.filter((r) => !r.hasOwnPhoto).length;

  const filtered = useMemo(() => {
    let list = rows;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((r) => r.name.toLowerCase().includes(q));
    }
    if (filter === "no-photo") list = list.filter((r) => !r.hasOwnPhoto);
    else if (filter === "with-photo") list = list.filter((r) => r.hasOwnPhoto);
    else if (filter === "disabled") list = list.filter((r) => r.disabledInChannel);
    return list;
  }, [rows, query, filter]);

  const body = (
    <>
      <div className="cluster" style={{ alignItems: "baseline", flexShrink: 0 }}>
        <div style={{ fontSize: "var(--text-lg)", fontWeight: 900 }}>Cores</div>
        <span className="card__meta">
          {totalInSisplan} no Sisplan · {semFoto} sem foto
        </span>
      </div>

      <div
        className="cluster"
        style={{
          background: "var(--color-soft)",
          border: ".0625rem solid var(--color-line)",
          borderRadius: "var(--radius-pill)",
          minHeight: "var(--size-tap-desktop)",
          padding: "0 .75rem",
          flexShrink: 0,
        }}
      >
        <Search className="icon" style={{ color: "var(--color-ink-3)" }} aria-hidden />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filtrar cor…"
          aria-label="Filtrar cor"
          style={{ flex: 1, minWidth: 0, border: 0, background: "transparent", outline: "none", fontSize: "var(--text-base)", fontWeight: 700, color: "var(--color-ink)" }}
        />
      </div>

      <div className="cluster" style={{ gap: ".25rem", flexShrink: 0 }}>
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className="chip"
            aria-pressed={filter === f.value}
            style={
              filter === f.value
                ? { background: "var(--color-coral-soft)", color: "var(--color-coral)" }
                : { cursor: "pointer" }
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <div
        onClick={() => onSelectContext("base")}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onSelectContext("base")}
        className="cluster"
        style={{
          minHeight: "2.75rem",
          flexShrink: 0,
          padding: ".375rem .625rem",
          borderRadius: "var(--radius-md)",
          cursor: "pointer",
          background: activeContext === "base" ? "var(--color-lilac-soft)" : "transparent",
          border: activeContext === "base" ? ".0625rem solid var(--color-lilac)" : ".0625rem solid transparent",
        }}
      >
        <span
          style={{
            width: "1.75rem",
            height: "1.75rem",
            borderRadius: "var(--radius-sm)",
            background: "var(--color-lilac-soft)",
            color: "var(--color-lilac)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          aria-hidden
        >
          ❑
        </span>
        <div className="grow">
          <div style={{ fontSize: "var(--text-base)", fontWeight: 900 }}>Base do produto</div>
          <div className="field__hint">Conteúdo, SEO e todas as fotos</div>
        </div>
        <span style={{ color: "var(--color-ink-3)" }} aria-hidden>→</span>
      </div>

      <div
        className="scroll-silent"
        style={{
          flex: 1,
          minHeight: "8rem",
          overflowY: "auto",
          borderTop: ".0625rem solid var(--color-line)",
          paddingTop: ".375rem",
          display: "flex",
          flexDirection: "column",
          gap: ".125rem",
        }}
      >
        {filtered.length === 0 ? (
          <p className="field__hint" style={{ padding: ".5rem" }}>Nenhuma cor bate com o filtro.</p>
        ) : (
          filtered.map((row) => {
            const isSel = selected.has(row.name);
            const isActive = activeContext === row.name;
            return (
              <div
                key={row.name}
                onClick={() => onSelectContext(row.name)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && onSelectContext(row.name)}
                title={row.disabledInChannel ? `${row.name} — desligada neste canal` : row.name}
                className={cn("row")}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.25rem 1.125rem minmax(0,1fr) auto",
                  gap: ".5rem",
                  alignItems: "center",
                  minHeight: "2.5rem",
                  padding: "0 .5rem",
                  borderRadius: "var(--radius-md)",
                  cursor: "pointer",
                  border: 0,
                  background: isActive ? "var(--color-coral-soft)" : "transparent",
                }}
              >
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelected(row.name);
                  }}
                  role="checkbox"
                  aria-checked={isSel}
                  aria-label={`Selecionar ${row.name}`}
                  style={{
                    width: "1rem",
                    height: "1rem",
                    borderRadius: ".25rem",
                    border: `.0625rem solid ${isSel ? "var(--color-coral)" : "var(--color-line)"}`,
                    background: isSel ? "var(--color-coral)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: ".6875rem",
                    fontWeight: 900,
                  }}
                >
                  {isSel ? "✓" : ""}
                </span>
                <ColorSwatch name={row.name} selected={isActive} />
                <span style={{ minWidth: 0 }}>
                  <span
                    className="truncate"
                    style={{
                      display: "block",
                      fontSize: "var(--text-base)",
                      fontWeight: 800,
                      color: isActive ? "var(--color-coral)" : "var(--color-ink)",
                    }}
                  >
                    {row.name}
                  </span>
                  <span style={{ display: "block", fontSize: "var(--text-sm)", fontWeight: 700, color: row.hasOwnPhoto ? "var(--color-ink-2)" : "var(--color-warn)" }}>
                    {row.disabledInChannel
                      ? "desligada neste canal"
                      : row.photoCount > 0
                        ? `${row.photoCount} foto${row.photoCount > 1 ? "s" : ""}`
                        : "sem foto própria"}
                  </span>
                </span>
                <span className="mono" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
                  {row.stock}
                </span>
              </div>
            );
          })
        )}
      </div>

      {selected.size > 0 ? (
        <div style={{ borderTop: ".0625rem solid var(--color-line)", paddingTop: ".625rem", flexShrink: 0 }} className="stack">
          <div style={{ fontSize: "var(--text-base)", fontWeight: 800 }}>{selected.size} cores selecionadas</div>
          <div className="cluster">
            <Button type="button" size="sm" variant="ok" disabled={disabled} onClick={onBatchEnable}>
              Ligar no canal
            </Button>
            <Button type="button" size="sm" variant="destructive" disabled={disabled} onClick={onBatchDisable}>
              Desligar
            </Button>
            <Select
              className="h-8 w-auto"
              value={copySource}
              disabled={disabled}
              onChange={(e) => setCopySource(e.target.value)}
              aria-label="Copiar fotos de qual cor?"
            >
              <option value="">Copiar fotos de…</option>
              {rows.filter((r) => r.hasOwnPhoto && !selected.has(r.name)).map((r) => (
                <option key={r.name} value={r.name}>{r.name}</option>
              ))}
            </Select>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={disabled || !copySource}
              onClick={() => {
                onBatchCopyPhotos(copySource);
                setCopySource("");
              }}
            >
              Copiar
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onClearSelected}>
              Limpar
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );

  if (!desktop) {
    return <div className="stack" style={{ gap: ".625rem" }}>{body}</div>;
  }

  return (
    <aside
      className="card scroll-silent"
      aria-label="Cores do produto"
      style={{
        // O sticky fica no wrapper (ProductPage); aqui so o teto de altura
        // com rolagem propria: header (3rem) + rodape fixo (~4.5rem) + folgas.
        maxHeight: "calc(100vh - 9.5rem)",
        display: "flex",
        flexDirection: "column",
        gap: ".625rem",
        minWidth: 0,
      }}
    >
      {body}
    </aside>
  );
}
